import type { WorkstationFuelNetworkSnapshot } from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import { t } from '../../state/i18n';
import { getBurnProgressRatio, getFuelIconPath } from './craftingPanelUtils';

const fuelIndicator = tv({
  slots: {
    container: [
      'absolute bottom-3 right-3',
      'flex flex-col items-end gap-1',
      'pointer-events-none',
      'z-40',
    ],

    labelRow: [
      'w-12',
      'flex items-center justify-center',
      'text-[10px] font-bold uppercase tracking-wide',
    ],
    label: ['text-ink-faded'],

    slotOuter: [
      'relative',
      'w-12 h-12',
      'bg-[#8b8b8b]',
      'border-2 border-[#555555]',
      'border-t-[#ffffff]',
      'border-l-[#ffffff]',
      'p-[2px]',
    ],

    slotInner: [
      'w-full h-full',
      'bg-[#c6c6c6]',
      'border border-[#8b8b8b]',
      'border-t-[#555555]',
      'border-l-[#555555]',
      'border-r-[#ffffff]',
      'border-b-[#ffffff]',
      'flex items-center justify-center',
      'relative',
      'overflow-hidden',
    ],

    icon: ['w-8 h-8 object-contain pixelated'],

    count: [
      'absolute right-0.5 bottom-0',
      'text-[11px] font-bold leading-none',
      'text-white',
      '[text-shadow:1px_1px_0_#000,0_1px_0_#000,1px_0_0_#000]',
    ],

    progressOuter: [
      'w-12 h-2',
      'bg-[#8b8b8b]',
      'border border-[#555555]',
      'border-t-[#ffffff]',
      'border-l-[#ffffff]',
      'p-[1px]',
    ],

    progressInner: ['w-full h-full', 'bg-[#2f2f2f]', 'relative', 'overflow-hidden'],

    progressFill: [
      'absolute left-0 top-0 h-full',
      'bg-gradient-to-r from-[#f39f2d] via-[#ffce52] to-[#ffe48a]',
      'transition-[width] duration-100',
    ],
  },
});

export const CraftingFuelIndicator = ({
  fuel,
  displayBurnRemainingMs,
}: {
  fuel: WorkstationFuelNetworkSnapshot;
  displayBurnRemainingMs: number;
}) => {
  const styles = fuelIndicator();
  const fuelIconPath = getFuelIconPath({ fuel });
  const burnProgressRatio = getBurnProgressRatio({
    displayBurnRemainingMs,
    burnDurationMs: fuel.burnDurationMs,
  });

  return (
    <div class={styles.container()}>
      <div class={styles.labelRow()}>
        <span class={styles.label()}>{t('ui.labels.fuel')}</span>
      </div>
      <div class={styles.slotOuter()}>
        <div class={styles.slotInner()}>
          {fuelIconPath && (
            <img src={fuelIconPath} alt={t('ui.labels.fuel')} class={styles.icon()} />
          )}
          <span class={styles.count()}>{fuel.fuelUnits}</span>
        </div>
      </div>
      <div class={styles.progressOuter()}>
        <div class={styles.progressInner()}>
          <div
            class={styles.progressFill()}
            style={{ width: `${Math.round(burnProgressRatio * 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
};
